import { useMemo } from 'react'
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Trash2,
} from 'lucide-react'
import { formatDateBR } from '../utils/dateUtils'
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type Row,
} from '@tanstack/react-table'
import { Button } from '@/components/ui/Button'
import { Pagination } from '@/components/ui/Pagination'
import { SkeletonTable } from '@/components/common/SkeletonTable'
import { GerencialTableEditCell } from './GerencialTableEditCell'
import type {
  CellValue,
  DirtyRowMap,
  EditableField,
  ValidationErrors,
} from '../hooks/useDirtyRows'

export interface GerencialRow {
  Id: number
  CodigoCliente: string | null
  Cliente: string | null
  Data: string | Date | null
  Valor: number | null
  Natureza: string | null
  Nota: string | null
  Empresa: string | null
  CentroResultado: string | null
  Job: string | null
}

export interface SortState {
  col: 'Data' | 'Valor'
  dir: 'asc' | 'desc'
}

interface GerencialTableProps {
  data: GerencialRow[]
  loading?: boolean
  mode?: 'read' | 'edit'
  sort: SortState
  onSortChange: (sort: SortState) => void
  page: number
  totalPages: number
  pageSize: number
  onPageChange: (page: number) => void
  onPageSizeChange?: (size: number) => void
  // edit mode props (required when mode === 'edit')
  dirty?: DirtyRowMap
  errors?: ValidationErrors
  onCellChange?: (rowId: number, field: EditableField, prev: CellValue, next: CellValue) => void
  onValidationChange?: (rowId: number, field: EditableField, message: string | null) => void
  selectedRowIds?: Set<number>
  onSelectionChange?: (rowIds: Set<number>) => void
  pendingDeletes?: Set<number>
  onToggleDelete?: (rowId: number) => void
}

function formatCurrency(value: number | null): string {
  if (value === null) return '—'
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)
}

const formatDate = formatDateBR

/**
 * Maps the view's "Nota" / "CentroResultado" (string) field names to the
 * base table's editable field names. Sent to the backend by buildPayload.
 *
 * View → Base mapping:
 *   Nota → NumeroNota
 *   CentroResultado (string in view) → CentroResultado (number in base, parseInt)
 *   Data, Valor, Natureza, Empresa → same name on base
 */
const FIELD_MAP_VIEW_TO_BASE: Record<string, EditableField> = {
  Data: 'Data',
  Valor: 'Valor',
  Natureza: 'Natureza',
  Empresa: 'Empresa',
  Nota: 'NumeroNota',
  CentroResultado: 'CentroResultado',
}

export function GerencialTable(props: GerencialTableProps) {
  const {
    data,
    loading,
    mode = 'read',
    sort,
    onSortChange,
    page,
    totalPages,
    pageSize,
    onPageChange,
    onPageSizeChange,
    dirty,
    errors,
    onCellChange,
    onValidationChange,
    selectedRowIds,
    onSelectionChange,
    pendingDeletes,
    onToggleDelete,
  } = props

  const isEdit = mode === 'edit'
  const isMarkedForDelete = (rowId: number) => pendingDeletes?.has(rowId) ?? false

  // Distinct values from the current page — fed into edit cell <datalist>
  // so users get autocomplete for free-text codes (Natureza, CentroResultado)
  // instead of having to remember/re-type them.
  const naturezaSuggestions = useMemo(() => {
    const set = new Set<string>()
    data.forEach(r => { if (r.Natureza) set.add(r.Natureza) })
    return Array.from(set).sort()
  }, [data])

  const centroResultadoSuggestions = useMemo(() => {
    const set = new Set<string>()
    data.forEach(r => { if (r.CentroResultado) set.add(r.CentroResultado) })
    return Array.from(set).sort()
  }, [data])

  const toggleRowSelection = (id: number) => {
    if (!onSelectionChange || !selectedRowIds) return
    const next = new Set(selectedRowIds)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    onSelectionChange(next)
  }

  const toggleAll = () => {
    if (!onSelectionChange || !selectedRowIds) return
    if (selectedRowIds.size === data.length) onSelectionChange(new Set())
    else onSelectionChange(new Set(data.map(r => r.Id)))
  }

  const columns = useMemo<ColumnDef<GerencialRow>[]>(() => {
    const cols: ColumnDef<GerencialRow>[] = []

    if (isEdit) {
      cols.push({
        id: '_select',
        header: () => (
          <input
            type="checkbox"
            checked={selectedRowIds ? selectedRowIds.size === data.length && data.length > 0 : false}
            ref={el => {
              if (el && selectedRowIds) {
                el.indeterminate = selectedRowIds.size > 0 && selectedRowIds.size < data.length
              }
            }}
            onChange={toggleAll}
            className="cursor-pointer"
            aria-label="Selecionar todos"
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            checked={selectedRowIds?.has(row.original.Id) ?? false}
            onChange={() => toggleRowSelection(row.original.Id)}
            className="cursor-pointer"
            aria-label={`Selecionar registro ${row.original.Id}`}
          />
        ),
      })
    }

    cols.push(
      {
        id: 'Data',
        header: () => <SortHeader label="Data" sortKey="Data" sort={sort} onSortChange={onSortChange} />,
        cell: ({ row }) =>
          isEdit ? (
            <EditViewCell
              row={row.original}
              field="Data"
              disabled={isMarkedForDelete(row.original.Id)}
              {...editCellProps(props)}
            />
          ) : (
            <span className="text-white">{formatDate(row.original.Data)}</span>
          ),
      },
      {
        id: 'Cliente',
        header: 'Cliente',
        cell: ({ row }) => <span className="text-white">{row.original.Cliente || '—'}</span>,
      },
      {
        id: 'Job',
        header: 'Job',
        cell: ({ row }) => <span className="text-white">{row.original.Job || '—'}</span>,
      },
      {
        id: 'Valor',
        header: () => <SortHeader label="Valor" sortKey="Valor" sort={sort} onSortChange={onSortChange} alignRight />,
        cell: ({ row }) => {
          if (isEdit) {
            return (
              <EditViewCell
                row={row.original}
                field="Valor"
                disabled={isMarkedForDelete(row.original.Id)}
                alignRight
                {...editCellProps(props)}
              />
            )
          }
          const v = row.original.Valor ?? 0
          const colorClass = v < 0 ? 'text-error' : v > 0 ? 'text-white' : 'text-brand-gray'
          return (
            <span className={`text-right font-mono block ${colorClass}`}>
              {formatCurrency(row.original.Valor)}
            </span>
          )
        },
      },
      {
        id: 'Natureza',
        header: 'Natureza',
        cell: ({ row }) =>
          isEdit ? (
            <EditViewCell
              row={row.original}
              field="Natureza"
              disabled={isMarkedForDelete(row.original.Id)}
              suggestions={naturezaSuggestions}
              {...editCellProps(props)}
            />
          ) : (
            <span className="text-white">{row.original.Natureza || '—'}</span>
          ),
      },
      {
        id: 'CentroResultado',
        header: 'C. Resultado',
        cell: ({ row }) =>
          isEdit ? (
            <EditViewCell
              row={row.original}
              field="CentroResultado"
              disabled={isMarkedForDelete(row.original.Id)}
              suggestions={centroResultadoSuggestions}
              {...editCellProps(props)}
            />
          ) : (
            <span className="text-white">{row.original.CentroResultado || '—'}</span>
          ),
      },
      {
        id: 'Nota',
        header: 'Nota',
        cell: ({ row }) =>
          isEdit ? (
            <EditViewCell
              row={row.original}
              field="Nota"
              disabled={isMarkedForDelete(row.original.Id)}
              {...editCellProps(props)}
            />
          ) : (
            <span className="text-white">{row.original.Nota || '—'}</span>
          ),
      },
    )

    // Trash icon column — only in edit mode (toggles "marked for delete";
    // actual deletion happens at Save). Read mode is intentionally
    // non-destructive: edits + deletes go through Modo Edição em Lote.
    if (isEdit && onToggleDelete) {
      cols.push({
        id: '_actions',
        header: '',
        cell: ({ row }) => {
          const marked = isMarkedForDelete(row.original.Id)
          return (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onToggleDelete(row.original.Id)}
              aria-label={marked ? 'Desmarcar exclusão' : 'Marcar para exclusão'}
              title={marked ? 'Desmarcar exclusão' : 'Excluir esta linha ao salvar'}
            >
              <Trash2 className={`w-4 h-4 ${marked ? 'text-error' : 'text-brand-gray hover:text-error'}`} />
            </Button>
          )
        },
      })
    }

    return cols
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, sort, onSortChange, dirty, errors, selectedRowIds, pendingDeletes, onToggleDelete, data.length])

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    pageCount: totalPages,
  })

  if (loading) {
    return <SkeletonTable rows={10} columns={7} />
  }

  return (
    <div className="space-y-4">
      <div className="overflow-auto rounded-monumenta border border-brand-dark-lighter bg-brand-dark">
        <table className="w-full text-sm">
          <thead>
            {table.getHeaderGroups().map(hg => (
              <tr key={hg.id} className="bg-brand-dark-light border-b border-brand-dark-lighter">
                {hg.headers.map(header => (
                  <th
                    key={header.id}
                    className="px-3 py-2 text-left text-xs font-semibold text-brand-gray uppercase tracking-wide whitespace-nowrap"
                  >
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map(row => (
              <RowRender
                key={row.id}
                row={row}
                isEdit={isEdit}
                isDirty={dirty?.has(row.original.Id) ?? false}
                isSelected={selectedRowIds?.has(row.original.Id) ?? false}
                isMarkedForDelete={isMarkedForDelete(row.original.Id)}
              />
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between gap-4">
        {onPageSizeChange && (
          <select
            value={pageSize}
            onChange={e => onPageSizeChange(Number(e.target.value))}
            className="bg-brand-dark-lighter border border-brand-dark-lighter rounded-md px-2 py-1 text-xs text-white focus:outline-none focus:ring-2 focus:ring-brand-primary"
          >
            {[30, 50, 100].map(n => (
              <option key={n} value={n}>{n} por página</option>
            ))}
          </select>
        )}
        {totalPages > 1 ? (
          <Pagination currentPage={page} totalPages={totalPages} onPageChange={onPageChange} />
        ) : <div />}
      </div>
    </div>
  )
}

interface RowRenderProps {
  row: Row<GerencialRow>
  isEdit: boolean
  isDirty: boolean
  isSelected: boolean
  isMarkedForDelete: boolean
}

function RowRender({ row, isEdit, isDirty, isSelected, isMarkedForDelete }: RowRenderProps) {
  const baseClasses = 'border-b border-brand-dark-lighter transition-colors'
  const deleteClasses = isMarkedForDelete
    ? 'bg-error/5 border-l-2 border-l-error opacity-70'
    : ''
  const dirtyClasses = !isMarkedForDelete && isDirty ? 'bg-warning/5 border-l-2 border-l-warning' : ''
  const selectedClasses = isSelected && !isDirty && !isMarkedForDelete ? 'border-l-2 border-l-warning/50' : ''
  const hoverClasses = isEdit ? 'hover:bg-brand-dark-light/50' : 'hover:bg-brand-dark-light/40'

  return (
    <tr className={`${baseClasses} ${deleteClasses} ${dirtyClasses} ${selectedClasses} ${hoverClasses}`}>
      {row.getVisibleCells().map(cell => (
        <td key={cell.id} className="px-3 py-2 align-middle">
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </td>
      ))}
    </tr>
  )
}

function SortHeader({
  label,
  sortKey,
  sort,
  onSortChange,
  alignRight,
}: {
  label: string
  sortKey: 'Data' | 'Valor'
  sort: SortState
  onSortChange: (sort: SortState) => void
  alignRight?: boolean
}) {
  const isActive = sort.col === sortKey
  const Icon = !isActive ? ArrowUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown
  const handleClick = () => {
    if (!isActive) onSortChange({ col: sortKey, dir: 'desc' })
    else onSortChange({ col: sortKey, dir: sort.dir === 'asc' ? 'desc' : 'asc' })
  }
  // `uppercase` and `tracking-wide` reapplied here because user-agent styles
  // for <button> reset `text-transform` to `none`, breaking the inheritance
  // from the parent <th> and producing mixed-case sortable headers vs
  // uppercase static headers.
  return (
    <button
      type="button"
      onClick={handleClick}
      className={`inline-flex items-center gap-1 uppercase tracking-wide font-semibold hover:text-white transition-colors ${alignRight ? 'justify-end w-full' : ''}`}
    >
      <span>{label}</span>
      <Icon size={12} className={isActive ? 'text-brand-primary' : 'text-brand-gray'} />
    </button>
  )
}

interface EditCellPropsBag {
  dirty?: DirtyRowMap
  errors?: ValidationErrors
  onCellChange?: GerencialTableProps['onCellChange']
  onValidationChange?: GerencialTableProps['onValidationChange']
}

function editCellProps(props: GerencialTableProps): EditCellPropsBag {
  return {
    dirty: props.dirty,
    errors: props.errors,
    onCellChange: props.onCellChange,
    onValidationChange: props.onValidationChange,
  }
}

interface EditViewCellProps extends EditCellPropsBag {
  row: GerencialRow
  field: keyof typeof FIELD_MAP_VIEW_TO_BASE
  alignRight?: boolean
  disabled?: boolean
  suggestions?: string[]
}

/**
 * Bridges the view-shaped row to the base-table EditableField when wiring
 * up GerencialTableEditCell. Lookup: view 'Nota' → base 'NumeroNota'.
 */
function EditViewCell({
  row,
  field,
  dirty,
  errors,
  onCellChange,
  onValidationChange,
  alignRight,
  disabled,
  suggestions,
}: EditViewCellProps) {
  const baseField = FIELD_MAP_VIEW_TO_BASE[field]
  if (!baseField || !onCellChange || !onValidationChange) {
    // Fall back to read-only render if edit handlers aren't wired up.
    return <span className="text-white">{getDisplayValue(row, field)}</span>
  }
  return (
    <GerencialTableEditCell
      rowId={row.Id}
      field={baseField}
      currentValue={getRawValue(row, field)}
      dirtyValue={dirty?.get(row.Id)?.[baseField]}
      errorMessage={errors?.get(row.Id)?.[baseField]}
      onChange={onCellChange}
      onValidate={onValidationChange}
      alignRight={alignRight}
      disabled={disabled}
      suggestions={suggestions}
    />
  )
}

function getRawValue(row: GerencialRow, field: keyof typeof FIELD_MAP_VIEW_TO_BASE): CellValue {
  switch (field) {
    case 'Data': return row.Data ? new Date(row.Data) : null
    case 'Valor': return row.Valor
    case 'Natureza': return row.Natureza
    case 'Empresa': return row.Empresa
    case 'Nota': return row.Nota
    case 'CentroResultado':
      return row.CentroResultado ? Number(row.CentroResultado) : null
    default: return null
  }
}

function getDisplayValue(row: GerencialRow, field: keyof typeof FIELD_MAP_VIEW_TO_BASE): string {
  switch (field) {
    case 'Data': return formatDate(row.Data)
    case 'Valor': return formatCurrency(row.Valor)
    default: return String((row as unknown as Record<string, unknown>)[field] ?? '—')
  }
}
